import { useContext, useMemo, useState, useEffect } from 'react';
import { ThemeContext } from '../../../../context/ThemeContext';
import {
  buildTokens,
  buildShell,
  contrastWithWhite,
  resolveTokens,
  resolveShell,
  DEFAULT_PALETTE_ID,
} from '../../../../utils/palette';
// The page speaks the same vocabulary as the rest of the rewritten
// admin area, so it reads that area's stylesheet rather than keeping
// a second copy of .pg-btn and friends.
import '../../Trainer/TrainerDashboard.css';
import './AppearanceSettings.css';
import DonutColorPicker from './DonutColorPicker';

// White button text needs 4.5:1 to pass AA at the sizes this app uses.
// Below that the custom accent is still allowed -- it is the user's
// interface -- but it is called out rather than silently accepted.
const AA_SMALL = 4.5;

const DEFAULT_CUSTOM_ACCENT = '#3B6FF0';

// Day and night, drawn with the same card a palette uses -- each shows
// the palette currently in use, in that mode -- so the choice between
// them looks exactly like every other choice on this page.
const MODE_CARDS = [
  {
    id: 'day',
    name: 'Day',
    blurb: 'Light surfaces and dark text. The page as it has always looked.',
  },
  {
    id: 'night',
    name: 'Night',
    blurb: 'Dark surfaces and light text, in the colours of your palette.',
  },
];

// A palette, drawn as the thing it will actually produce: a card on its
// ground, its own line colour, a filled button, a selected row. Small
// enough to sit four-up, honest enough that picking one holds no
// surprises.
const PaletteCard = ({ palette, tokens, shell, isActive, onChoose }) => (
  <button
    type="button"
    className={`ap-card${isActive ? ' is-active' : ''}`}
    onClick={onChoose}
    aria-pressed={isActive}
    style={{
      '--c-ground': tokens['--pg-ground'],
      '--c-card': tokens['--pg-card'],
      '--c-line': tokens['--pg-line'],
      '--c-accent': tokens['--pg-accent'],
      '--c-soft': tokens['--pg-accent-soft'],
      '--c-zebra': tokens['--pg-zebra'],
      '--c-rail': shell['--rail'],
      '--c-nav': shell['--nav-active'],
    }}
  >
    {/* The rail is drawn beside the card, not left out: it is the
        largest block of colour on screen, and a palette that did
        not show it would be lying about what choosing it does. */}
    <span className="ap-card-preview" aria-hidden="true">
      <span className="ap-prev-rail">
        <span className="ap-prev-nav" />
        <span className="ap-prev-navline" />
        <span className="ap-prev-navline" />
      </span>
      <span className="ap-prev-main">
        <span className="ap-prev-bar">
          <span className="ap-prev-dot" />
          <span className="ap-prev-pill" />
        </span>
        <span className="ap-prev-row" />
        <span className="ap-prev-row ap-prev-row--zebra" />
        <span className="ap-prev-row ap-prev-row--soft" />
      </span>
    </span>

    <span className="ap-card-id">
      <span className="ap-card-name">
        {palette.name}
        {isActive && <span className="ap-card-flag">In use</span>}
      </span>
      <span className="ap-card-blurb">{palette.blurb}</span>
    </span>
  </button>
);

const AppearanceSettings = () => {
  const {
    palette,
    palettes,
    selectPalette,
    setCustomPalette,
    resetPalette,
    mode,
    setMode,
    nightModeAvailable,
  } = useContext(ThemeContext);

  const isCustom = palette?.id === 'custom';

  // The palette in use, resolved in both modes, for the Day / Night cards.
  const modeTokens = useMemo(
    () => ({ day: resolveTokens(palette, 'day'), night: resolveTokens(palette, 'night') }),
    [palette],
  );
  const currentShell = useMemo(() => resolveShell(palette), [palette]);

  // The builder's own working values. Seeded from the saved custom
  // palette when there is one, so reopening the page does not throw
  // away what the user mixed last time.
  const [accent, setAccent] = useState(
    isCustom ? palette.accent : DEFAULT_CUSTOM_ACCENT,
  );
  const [tint, setTint] = useState(isCustom ? palette.tint : 100);

  useEffect(() => {
    if (palette?.id === 'custom') {
      setAccent(palette.accent);
      setTint(palette.tint);
    }
  }, [palette]);

  const customTokens = useMemo(() => buildTokens(accent, tint), [accent, tint]);
  // The rail follows the accent only -- the tint slider moves the page
  // surfaces, not the sidebar, which is always dark.
  const customShell = useMemo(() => buildShell(accent), [accent]);
  const contrast = useMemo(() => contrastWithWhite(accent), [accent]);
  const contrastOk = contrast >= AA_SMALL;

  // Applying is what writes it; dragging the picker only moves the
  // swatch. A live repaint of the whole app under the pointer is
  // more movement than it is worth on a settings page.
  const applyCustom = () => setCustomPalette(accent, tint);

  const isDefault = !isCustom && (palette?.id || DEFAULT_PALETTE_ID) === DEFAULT_PALETTE_ID;

  return (
    <div className="pg ap">
      <header className="pg-head">
        <div className="pg-head-main">
          <h4 className="pg-title">Appearance</h4>
          <p className="pg-sub">
            Choose a palette, or mix your own. The choice is yours alone
            &mdash; it is saved in this browser and changes nothing for
            anyone else.
          </p>
        </div>
        <div className="pg-head-actions">
          <button
            type="button"
            className="pg-btn"
            onClick={resetPalette}
            disabled={isDefault}
          >
            Reset to default
          </button>
        </div>
      </header>

      {/* Drawn only once night mode is available (NIGHT_MODE_READY in
          utils/palette.js, or the developer preview unlock). Until then a
          night page would be half converted, so there is nothing to offer. */}
      {nightModeAvailable && (
        <section className="ap-section" aria-labelledby="ap-mode-title">
          <h5 className="ap-section-title" id="ap-mode-title">
            Day or night
          </h5>
          <div className="ap-grid">
            {MODE_CARDS.map((m) => (
              <PaletteCard
                key={m.id}
                palette={m}
                tokens={modeTokens[m.id]}
                shell={currentShell}
                isActive={mode === m.id}
                onChoose={() => setMode(m.id)}
              />
            ))}
          </div>
        </section>
      )}

      <section className="ap-section" aria-labelledby="ap-ready-title">
        <h5 className="ap-section-title" id="ap-ready-title">
          Ready-made
        </h5>
        <div className="ap-grid">
          {palettes.map((p) => (
            <PaletteCard
              key={p.id}
              palette={p}
              tokens={p.tokens}
              shell={p.shell}
              isActive={!isCustom && (palette?.id || DEFAULT_PALETTE_ID) === p.id}
              onChoose={() => selectPalette(p.id)}
            />
          ))}
        </div>
      </section>

      <section className="ap-section" aria-labelledby="ap-custom-title">
        <h5 className="ap-section-title" id="ap-custom-title">
          Mix your own
          {isCustom && <span className="ap-section-flag">In use</span>}
        </h5>

        <div className="ap-builder">
          <div className="ap-builder-controls">
            <div className="pg-field">
              <span className="pg-label" id="ap-accent-label">
                Accent colour
              </span>
              {/* Only ever reports a complete hex, so the preview and
                  the contrast check below never see a half-typed one. */}
              <DonutColorPicker
                value={accent}
                onChange={setAccent}
                contrastTarget={AA_SMALL}
                labelledBy="ap-accent-label"
              />
              <p className="ap-hint">
                Buttons, links, the active menu item and focus rings.
              </p>
            </div>

            <div className="pg-field">
              <label className="pg-label" htmlFor="ap-tint">
                Surface tint &mdash; {tint}%
              </label>
              <input
                id="ap-tint"
                type="range"
                className="ap-range"
                min="0"
                max="100"
                step="5"
                value={tint}
                onChange={(e) => setTint(Number(e.target.value))}
              />
              <p className="ap-hint">
                How much of the accent&rsquo;s hue carries into the page
                ground, table stripes and borders. At 0 they are plain grey.
              </p>
            </div>

            {/* Only the accent can fail, and only against white button
                text: every surface keeps its lightness whatever is picked,
                so body text never loses contrast. */}
            <div
              className={`ap-check${contrastOk ? ' is-ok' : ' is-warn'}`}
              role="status"
            >
              <b>{contrast}:1</b>
              <span>
                {contrastOk
                  ? 'White button text is legible on this accent.'
                  : `White button text falls below the ${AA_SMALL}:1 standard. A darker accent will fix it.`}
              </span>
            </div>

            <div className="ap-builder-actions">
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={applyCustom}
              >
                Apply this palette
              </button>
            </div>
          </div>

          {customTokens && customShell && (
            <div
              className="ap-preview"
              style={{
                '--c-rail': customShell['--rail'],
                '--c-nav': customShell['--nav-active'],
                '--c-navlabel': customShell['--sidebar-label'],
                '--c-ground': customTokens['--pg-ground'],
                '--c-card': customTokens['--pg-card'],
                '--c-line': customTokens['--pg-line'],
                '--c-line-soft': customTokens['--pg-line-soft'],
                '--c-accent': customTokens['--pg-accent'],
                '--c-accent-hover': customTokens['--pg-accent-hover'],
                '--c-soft': customTokens['--pg-accent-soft'],
                '--c-zebra': customTokens['--pg-zebra'],
                '--c-sunken': customTokens['--pg-sunken'],
              }}
            >
              {/* A real page in miniature, not a row of swatches: the
                  question being answered is "what will my screen look
                  like", and swatches do not answer it. The sidebar is
                  in shot because it is the biggest thing on screen. */}
              <div className="ap-preview-shell">
              <div className="ap-preview-rail" aria-hidden="true">
                <span className="ap-rail-brand" />
                <span className="ap-rail-label">MAIN MENU</span>
                <span className="ap-rail-item is-on" />
                <span className="ap-rail-item" />
                <span className="ap-rail-item" />
              </div>
              <div className="ap-preview-card">
                <div className="ap-preview-head">
                  <span className="ap-preview-title">Attendance</span>
                  <span className="ap-preview-btn">Apply</span>
                </div>
                <div className="ap-preview-table">
                  <div className="ap-preview-tr ap-preview-tr--head">
                    <span>Batch</span>
                    <span>Date</span>
                    <span>Summary</span>
                  </div>
                  <div className="ap-preview-tr">
                    <span>Java Full Stack</span>
                    <span>12 Sep 2026</span>
                    <span className="ap-preview-tally">
                      <i className="ap-chip ap-chip--good">P 18</i>
                      <i className="ap-chip ap-chip--bad">A 2</i>
                    </span>
                  </div>
                  <div className="ap-preview-tr ap-preview-tr--zebra">
                    <span>Python Evening</span>
                    <span>11 Sep 2026</span>
                    <span className="ap-preview-tally">
                      <i className="ap-chip ap-chip--good">P 14</i>
                      <i className="ap-chip ap-chip--bad">A 5</i>
                    </span>
                  </div>
                  <div className="ap-preview-tr ap-preview-tr--sel">
                    <span>Data Science</span>
                    <span>10 Sep 2026</span>
                    <span className="ap-preview-tally">
                      <i className="ap-chip ap-chip--good">P 21</i>
                      <i className="ap-chip ap-chip--bad">A 1</i>
                    </span>
                  </div>
                </div>
              </div>
              </div>
              <p className="ap-preview-note">
                The sidebar takes the accent&rsquo;s hue but stays dark, and
                its white text keeps the contrast it has today. Present and
                absent keep their colours in every palette &mdash; they mean
                something, so they are not yours to change.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AppearanceSettings;
